import {
    type CaseStatusAnswerElement,
    type GetCaseDetailsResponse,
    type GetCaseSummariesRequest,
    type Retailer,
    type TechlineStatusCode as TechlineStatusCodeDto,
} from '@packages/clients';

import type { WorksheetFormElementEntity, WorksheetFormEntity } from '../../../worksheets/forms';
import type { TechlineCaseEntity } from '../entities';
import type { CaseSubmissionEntry } from '../operations/types';
import type {
    CaseSummariesRequestParams,
    CaseUpdateBodyParams,
    CidCvnSubmissionBodyParams,
    CidCvnUpdateBodyParams,
    CloseCaseBodyParams,
    CreateCaseBodyParams,
    FailCodeUpdateBodyParams,
} from './types';

import { TechlineStatusCode } from '../types';
import { statusNormalizationMap } from './constants';
import { formatCaseDate } from './formatters';

/**
 * Build a flat submissions array from WorksheetSubmissionGroups for case
 * submission. Each group's titles are flattened into individual submission
 * entries with answer data extracted from the cached submission map.
 *
 * @param worksheetGroups - the worksheet submission groups from case details
 * @param submissionMap - cached worksheet submissions keyed by submission ID
 * @returns the flattened submissions array for the API
 * @example
 * ```ts
 * const submissions = buildCaseSubmissionPayload(
 *     caseData.WorksheetSubmissionGroups,
 *     worksheetSubmissionMap
 * );
 * ```
 */
export function buildCaseSubmissionPayload(
    worksheetGroups: TechlineCaseEntity['WorksheetSubmissionGroups'],
    submissionMap: Map<string, WorksheetFormEntity>
): CaseSubmissionEntry[] {
    return (
        worksheetGroups?.flatMap((group) =>
            group.worksheetSubmissionTitles.map((worksheet) => {
                // Look up the cached submission to extract its answers
                const formEntity = submissionMap.get(worksheet.submissionId);
                const answers = formEntity ? extractAnswersFromElements(formEntity.elements) : [];

                return {
                    answers,
                    submissionId: worksheet.submissionId,
                    worksheetId: worksheet.worksheetId,
                };
            })
        ) ?? []
    );
}

/**
 * Build the query parameters for fetching a list of cases. Maps the selected status filters into the expected API
 * format and includes pagination parameters.
 *
 * @param params - the fetch cases query parameters
 * @returns the API request parameters
 * @example
 * ```ts
 * const params = buildCaseSummariesRequest({ retailerNumber: '99', vin: 'ABC', techlineCaseStatus: [TechlineStatusCode.IN_REVIEW, TechlineStatusCode.PENDING_TECHLINE], page: '1', recordCount: '10' });
 * ```
 */
export function buildCaseSummariesRequest(params: CaseSummariesRequestParams): GetCaseSummariesRequest {
    const { techlineCaseStatus, page, recordCount, ...filters } = params;

    return {
        ...filters,
        page: page ?? '1',
        recordCount: recordCount ?? '10',
        ...(techlineCaseStatus?.length
            ? { techlineCaseStatus: techlineCaseStatus.map((s) => s.replace(/ /g, '_')).join(',') }
            : {}),
    };
}

/**
 * Build the request body for updating a techline case. Spreads the current
 * case data and overlays identity fields.
 *
 * @param params - the case data and identity fields
 * @returns the API request body
 * @example
 * ```ts
 * const body = buildCaseUpdateBody({ techlineCaseData, caseNumber: '123', vin: 'ABC', username: 'jdoe', retailerNumber: '99' });
 * ```
 */
export function buildCaseUpdateBody(params: CaseUpdateBodyParams): Record<string, unknown> {
    const { techlineCaseData, caseNumber, vin, username, retailerNumber } = params;
    return {
        ...techlineCaseData,
        caseNumber,
        vin,
        username,
        retailernumber: retailerNumber,
    };
}

/**
 * Build the request body for submitting a CID-CVN case. Transitions the
 * case to IN_REVIEW status with a single worksheet submission entry.
 *
 * @param params - the submission details and identity fields
 * @returns the API request body
 * @example
 * ```ts
 * const body = buildCidCvnSubmissionBody({ caseNumber: '123', username: 'jdoe', retailerNumber: '99', submissionId: 's1', worksheetId: 'w1' });
 * ```
 */
export function buildCidCvnSubmissionBody(params: CidCvnSubmissionBodyParams): Record<string, unknown> {
    const { caseNumber, username, retailerNumber, submissionId, worksheetId } = params;
    return {
        casenumber: caseNumber,
        username,
        techlinecasestatus: TechlineStatusCode.IN_REVIEW,
        retailernumber: retailerNumber,
        submissions: [{ submissionId, worksheetId }],
    };
}

/**
 * Build the request body for saving CID-CVN case details. Spreads form
 * data fields directly into the payload alongside identity fields.
 *
 * @param params - the CID-CVN form data and identity fields
 * @returns the API request body
 * @example
 * ```ts
 * const body = buildCidCvnUpdateBody({ caseNumber: '123', vin: 'ABC', username: 'jdoe', retailerNumber: '99', formData: { repairOrderNumber: 'RO1', repairOrderOpenDate: '2026-01-01', mileage: '50000' } });
 * ```
 */
export function buildCidCvnUpdateBody(params: CidCvnUpdateBodyParams): Record<string, unknown> {
    const { caseNumber, vin, username, retailerNumber, formData } = params;
    return {
        caseNumber,
        vin,
        username,
        retailernumber: retailerNumber,
        ...formData,
    };
}

/**
 * Build the request body for closing a techline case. Includes the closure
 * description, vehicle repair status, and a formatted closure date.
 *
 * @param params - the closure details
 * @returns the API request body
 * @example
 * ```ts
 * const body = buildCloseCaseBody({ caseNumber: '123', username: 'jdoe', retailerNumber: '99', description: 'Resolved', isVehicleRepaired: true, closureDate: new Date() });
 * ```
 */
export function buildCloseCaseBody(params: CloseCaseBodyParams): Record<string, unknown> {
    const { caseNumber, username, retailerNumber, description, isVehicleRepaired, closureDate } = params;
    return {
        casenumber: caseNumber,
        username,
        retailernumber: retailerNumber,
        techlinecasestatus: TechlineStatusCode.CLOSE_RESOLVED,
        techsharecaseclosuredescription: description,
        closuredate: formatCaseDate(closureDate),
        wasTheVehicleRepaired: isVehicleRepaired,
    };
}

/**
 * Build the request body for creating a new techline case. Includes the VIN, retailer number, and username of the creator.
 *
 * @param params - the creation details and identity fields
 * @returns the API request body
 * @example
 * ```ts
 * const body = buildCreateCaseBody({ vin: 'ABC', retailerNumber: '99', username: 'jdoe' });
 * ```
 */
export function buildCreateCaseBody(params: CreateCaseBodyParams): Record<string, unknown> {
    const { vin, retailerNumber, username } = params;
    return {
        vin,
        retailernumber: retailerNumber,
        username,
    };
}

/**
 * Build the request body for updating fail code fields on a case.
 * Spreads the fail code data fields directly into the payload alongside
 * identity fields.
 *
 * @param params - the fail code data and identity fields
 * @returns the API request body
 * @example
 * ```ts
 * const body = buildFailCodeUpdateBody({ caseNumber: '123', vin: 'ABC', username: 'jdoe', retailerNumber: '99', failCodeData: { failCode: 'F1', repairCode: 'R1', section: 'Body', subSection: 'Door' } });
 * ```
 */
export function buildFailCodeUpdateBody(params: FailCodeUpdateBodyParams): Record<string, unknown> {
    const { caseNumber, vin, username, retailerNumber, failCodeData } = params;
    return {
        caseNumber,
        vin,
        username,
        retailernumber: retailerNumber,
        ...failCodeData,
    };
}

/**
 * Extract answer data from a worksheet form entity's elements into the
 * flat answer element shape expected by the case submission API. Maps
 * each element's selected options and grid cell answers.
 *
 * @param elements - the form elements containing the user's answers
 * @returns the answer elements for the case submission payload
 * @example
 * ```ts
 * const answers = extractAnswersFromElements(formEntity.elements);
 * // [{ elementId: 'abc', selectedOptions: [...], worksheetGridCellAnswers: [...] }]
 * ```
 */
export function extractAnswersFromElements(elements: WorksheetFormElementEntity[]): CaseStatusAnswerElement[] {
    return elements.map((element) => ({
        elementId: element.worksheetElementId,
        selectedOptions: element.selectedOptions?.map((opt) => ({
            answerText: opt.answerText,
            optionId: opt.optionId,
            optionLabel: opt.optionLabel,
        })),
        worksheetGridCellAnswers: element.worksheetGridCellAnswers?.map((cell) => ({
            value: cell.value,
            worksheetElementGridId: cell.worksheetElementGridId,
        })),
    }));
}

/**
 * Inverted lookup map derived from `statusNormalizationMap`. Maps each legacy/NS DTO value to its
 * official domain status for O(1) lookup. Built once at module load.
 */
const dtoToOfficialStatus = new Map<TechlineStatusCodeDto, TechlineStatusCode>(
    Object.entries(statusNormalizationMap).flatMap(([official, dtoValues]) =>
        dtoValues.map((dto): [TechlineStatusCodeDto, TechlineStatusCode] => [dto, official as TechlineStatusCode])
    )
);

/** The set of official domain status string values, for fast membership checks. */
const officialStatusValues = new Set<string>(Object.values(TechlineStatusCode));

/**
 * Normalize the polymorphic `caseDetails` field from the retrieve-case API.
 * The API returns either a plain `Retailer` object or a wrapper with a
 * `results` property containing the `Retailer`. This function unwraps
 * the wrapper when present.
 *
 * @param caseDetails - the raw caseDetails field from RetrieveCaseResponse
 * @returns the unwrapped Retailer object
 */
export function normalizeCaseDetailsResponse(caseDetails: GetCaseDetailsResponse['caseDetails']): Retailer {
    if ('results' in caseDetails) {
        return caseDetails.results;
    }
    return caseDetails;
}

/**
 * Normalizes a DTO status code (which may be a legacy or NorthStar value) into the official
 * domain `TechlineStatusCode`. Throws if the value is not recognized as official, legacy, or NS.
 *
 * @param status - the raw status code from the API response
 * @throws {Error} if the status is not in the official set or the normalization map
 * @returns the normalized official status
 * @example
 * ```ts
 * normalizeTechlineStatus(TechlineStatusCodeDto.LEGACY_OPEN); // TechlineStatusCode.IN_REVIEW
 * normalizeTechlineStatus(TechlineStatusCodeDto.DRAFT);        // TechlineStatusCode.DRAFT
 * ```
 */
export function normalizeTechlineStatus(status: TechlineStatusCodeDto): TechlineStatusCode {
    // Already an official status; pass through
    if (officialStatusValues.has(status)) {
        return status as unknown as TechlineStatusCode;
    }

    // Check the normalization map
    const normalized = dtoToOfficialStatus.get(status);
    if (normalized) {
        return normalized;
    }

    throw new Error(`Unknown TechlineStatusCode: "${status}". Update statusNormalizationMap to handle this value.`);
}
